import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { Star, Check, Copy, PlusCircle } from "lucide-react";
import colorBlind from "color-blind";
import {
  hexToRgb,
  hexToHsl,
  getWadaColorName,
  getContrastRatio,
} from "../lib/colorUtils";

const ContextMenu = ({ x, y, items, onClose, triggerElement }) => {
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ x, y });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    const updatePosition = () => {
      if (triggerElement && triggerElement.current) {
        const rect = triggerElement.current.getBoundingClientRect();
        // Keep the menu relative to the trigger element
        setPosition({ x: rect.right + 8, y: rect.top });
      }
    };

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, triggerElement]);

  return createPortal(
    <div
      ref={menuRef}
      className="fixed bg-white dark:bg-gray-800 shadow-md rounded-md p-2 z-50"
      style={{
        top: `${position.y}px`,
        left: `${position.x}px`,
      }}
    >
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li
            key={index}
            className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded p-1 transition-colors duration-300"
            onClick={() => {
              item.action();
              onClose();
            }}
          >
            {React.cloneElement(item.icon, { className: "w-4 h-4" })}
            <span>{item.label}</span>
          </li>
        ))}
      </ul>
    </div>,
    document.body,
  );
};

const ColorSwatch = ({
  color,
  number,
  onClick,
  isBaseColor,
  visibleFields,
  colorBlindnessType,
  isDark,
  onAddToPalette,
  onAddToCustomPalette,
}) => {
  const [copied, setCopied] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const swatchRef = useRef(null);

  const simulateColorBlindness = useCallback((color, type) => {
    if (type === "normal") return color;
    const simulateFunc = colorBlind[type];
    return simulateFunc ? simulateFunc(color) : color;
  }, []);

  const simulatedColor = useMemo(
    () => simulateColorBlindness(color, colorBlindnessType),
    [color, colorBlindnessType, simulateColorBlindness],
  );

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(text);
      setTimeout(() => setCopied(null), 2000);
    });
  }, []);

  const rgb = useMemo(() => hexToRgb(color), [color]);
  const hsl = useMemo(() => hexToHsl(color), [color]);
  const colorName = useMemo(
    () => getWadaColorName(hsl[0], hsl[1], hsl[2]),
    [hsl],
  );

  // Calculate contrast with white and black
  const contrastWithWhite = useMemo(
    () => getContrastRatio(rgb, [255, 255, 255]),
    [rgb],
  );
  const contrastWithBlack = useMemo(
    () => getContrastRatio(rgb, [0, 0, 0]),
    [rgb],
  );

  // Choose text color based on contrast
  const textColor = useMemo(
    () => (contrastWithWhite > contrastWithBlack ? "white" : "black"),
    [contrastWithWhite, contrastWithBlack],
  );

  // Function to determine if contrast passes WCAG standards
  const getContrastInfo = useCallback((ratio) => {
    const passesAA = ratio >= 4.5;
    const passesAALarge = ratio >= 3;
    return {
      color: passesAA ? "text-green-500" : "text-red-500",
      text: `${ratio.toFixed(2)} ${passesAA ? "✓" : "✗"} AA${passesAALarge ? " (AA Large ✓)" : ""}`,
    };
  }, []);

  const contrastWithWhiteInfo = useMemo(
    () => getContrastInfo(contrastWithWhite),
    [contrastWithWhite, getContrastInfo],
  );
  const contrastWithBlackInfo = useMemo(
    () => getContrastInfo(contrastWithBlack),
    [contrastWithBlack, getContrastInfo],
  );

  // Determine which contrast to show based on the current theme
  const relevantContrast = useMemo(
    () => (isDark ? contrastWithBlackInfo : contrastWithWhiteInfo),
    [isDark, contrastWithBlackInfo, contrastWithWhiteInfo],
  );

  // ContextMenu
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    setMenuPosition({ x: e.clientX, y: e.clientY });
    setShowMenu(true);
  }, []);

  const menuItems = useMemo(() => {
    const items = [
      {
        icon: <Copy />,
        label: "Copy HEX",
        action: () => copyToClipboard(color),
      },
      {
        icon: <PlusCircle />,
        label: "Add to visualizer",
        action: () => {
          if (typeof onAddToPalette === "function") {
            onAddToPalette(color);
          } else {
            console.error("onAddToPalette is not a function");
          }
        },
      },
    ];

    // Only add "Add to Custom Palette" if the function is provided
    if (typeof onAddToCustomPalette === "function") {
      items.push({
        icon: <PlusCircle />,
        label: "Add to custom palette",
        action: () => onAddToCustomPalette(color),
      });
    }

    return items;
  }, [color, copyToClipboard, onAddToPalette, onAddToCustomPalette]);

  return (
    <div className="w-36 text-left">
      <div
        ref={swatchRef}
        className="w-36 h-36 rounded-md cursor-pointer transition-transform hover:scale-105 mb-2 relative"
        style={{ backgroundColor: simulatedColor }}
        title={color}
        onClick={() => onClick(color)}
        onContextMenu={handleContextMenu}
      >
        {number !== undefined && (
          <span
            className="absolute top-2 left-2 text-xs font-bold"
            style={{ color: textColor }}
          >
            {number}
          </span>
        )}
        {isBaseColor && (
          <Star
            className="absolute top-2 right-2 w-4 h-4"
            style={{ color: textColor }}
          />
        )}
        {visibleFields.colorName && (
          <div
            className="absolute bottom-0 left-0 right-0 p-2 text-xs font-medium break-words"
            style={{ color: textColor }}
          >
            {colorName}
          </div>
        )}
      </div>
      <div className="text-xs space-y-0.5">
        {visibleFields.hex && (
          <div
            className="cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 rounded p-1 transition-colors duration-300"
            onClick={() => copyToClipboard(color)}
          >
            <span className="font-bold">HEX:</span> {color}
          </div>
        )}
        {visibleFields.rgb && (
          <div
            className="cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 rounded p-1"
            onClick={() => copyToClipboard(`rgb(${rgb.join(", ")})`)}
          >
            <span className="font-bold">RGB:</span> {rgb.join(", ")}
          </div>
        )}
        {visibleFields.hsl && (
          <div
            className="cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 rounded p-1"
            onClick={() => copyToClipboard(`hsl(${hsl.join(", ")})`)}
          >
            <span className="font-bold">HSL:</span> {hsl.join(", ")}
          </div>
        )}
        {visibleFields.contrastRatio && (
          <div className="p-1 transition-colors duration-300">
            <div>Contrast ratio:</div>
            <div
              className={`${relevantContrast.color} transition-colors duration-300`}
            >
              {isDark ? "Black" : "White"} bg: {relevantContrast.text}
            </div>
          </div>
        )}
      </div>
      {showMenu && (
        <ContextMenu
          x={menuPosition.x}
          y={menuPosition.y}
          items={menuItems}
          onClose={() => setShowMenu(false)}
          triggerElement={swatchRef}
        />
      )}
      {copied && (
        <div className="fixed bottom-4 right-4 bg-gray-950 text-white px-4 py-2 rounded-md shadow-lg flex items-center transition-opacity duration-300">
          <Check className="w-4 h-4 mr-2" />
          Copied to clipboard: {copied}
        </div>
      )}
    </div>
  );
};

export default ColorSwatch;
